import React, { useState } from "react"
import { Tag, Popover, Tooltip, Image, Space, Button } from "antd"
import {
  EditOutlined,
  TagOutlined,
  InboxOutlined,
  AppstoreOutlined,
  EnvironmentOutlined,
  BarcodeOutlined,
  DollarOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  SwapOutlined,
  DeploymentUnitOutlined,
  EyeOutlined,
} from "@ant-design/icons"
import { TInventoryFirestore, TStorageItemRS } from "../../../models/storageModel"
import { labelStyle, valueStyle, rowStyle } from "./viewItemModalHelpers"

export const InfoRow = ({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode
  label: string
  children: React.ReactNode
}) => (
  <div style={rowStyle}>
    <div style={{ paddingTop: 2, color: "#bfbfbf", flexShrink: 0 }}>{icon}</div>
    <div style={{ flex: 1 }}>
      <div style={labelStyle}>{label}</div>
      <div style={valueStyle}>{children}</div>
    </div>
  </div>
)

export interface ViewProductProps {
  item: TStorageItemRS
  onEdit: () => void
  onFractionate?: () => void
  canEdit: boolean
  register: any
  store: any
}

export const ViewProductReadOnly: React.FC<ViewProductProps> = ({
  item,
  onEdit,
  onFractionate,
  canEdit,
  register,
  store,
}) => {
  const getCategoryName = (key: string) =>
    register.categoryOptions.find((o: any) => o.key === key)?.string_value || key || "—"

  const getTypeName = (key: string) =>
    register.itemTypes.find((o: any) => o.key === key)?.string_value || key || "—"

  const getSellPrice = () =>
    item.prices?.find((p: any) => p.type === "SELL")?.value ?? item.sell_price

  const getPurchasePrice = () =>
    item.prices?.find((p: any) => p.type === "PURCHASE")?.value ?? item.purchase_price

  const formatCOP = (v?: number) =>
    v === undefined ? "—" : `$${v.toLocaleString("es-CO")} COP`

  const renderStock = (inventory: TInventoryFirestore[], register_type: string) => {
    if (register_type === "SERVICE") return <Tag color="default">No aplica</Tag>

    const total = (!inventory || inventory.length === 0)
      ? 0
      : inventory.reduce((s, e) => s + e.quantity, 0)

    let warningIcon = null
    if (total === 0) {
      warningIcon = (
        <Tooltip title="No hay unidades disponibles. Puedes agregarlas desde el módulo de storage haciendo un movimiento de entrada.">
          <ExclamationCircleOutlined style={{ color: '#faad14', marginLeft: 6 }} />
        </Tooltip>
      )
    } else if (total < 0) {
      warningIcon = (
        <Tooltip title="Se hicieron movimientos sin tener unidades. Debes reabastecer con un movimiento desde el módulo de storage.">
          <ExclamationCircleOutlined style={{ color: '#f5222d', marginLeft: 6 }} />
        </Tooltip>
      )
    }

    if (!inventory || inventory.length === 0 || inventory.length === 1) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>{total}</span>
          {warningIcon}
        </div>
      )
    }

    const content = (
      <div style={{ minWidth: 180 }}>
        {inventory.map((entry) => {
          const name = store.list.find((s: any) => s.uid === entry.uid)?.name || entry.uid
          return (
            <div key={entry.uid} style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "2px 0" }}>
              <span>{name}</span>
              <strong>{entry.quantity}</strong>
            </div>
          )
        })}
      </div>
    )
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Popover title="Existencias por almacén" content={content} trigger="hover">
          <span style={{ cursor: "pointer", borderBottom: "1px dashed #1890ff", color: "#1890ff" }}>{total}</span>
        </Popover>
        {warningIcon}
      </div>
    )
  }

  const productImages = (item.images && item.images.length > 0)
    ? item.images
    : (item.image ? [{ file_name: item.name || "Foto", file_url: item.image, is_primary: true, display_order: 0, storage_path: "" }] : [])
  const primaryImage = productImages.find((img: any) => img.is_primary) || productImages[0]
  const [activeImgIndex, setActiveImgIndex] = useState(0)
  const activeImage = productImages[activeImgIndex] || primaryImage

  return (
    <div style={{ padding: "0 4px 8px" }}>
      {/* Encabezado con Nombre e ID */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 600, color: "#262626" }}>{item.name || "—"}</div>
        {item.custom_id && (
          <div style={{ fontSize: 13, color: "#8c8c8c", marginTop: 2 }}>
            ID: {item.custom_id}
          </div>
        )}
      </div>

      {/* Galería de Imágenes en Vista Detalle */}
      {productImages.length > 0 ? (
        <div
          style={{
            background: "#fafafa",
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
            border: "1px solid #f0f0f0",
          }}
        >
          <div
            style={{
              height: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#ffffff",
              borderRadius: 6,
              overflow: "hidden",
              marginBottom: productImages.length > 1 ? 10 : 0,
            }}
          >
            <Image
              src={activeImage?.file_url}
              alt={item.name}
              style={{
                maxHeight: 200,
                width: "100%",
                objectFit: "contain",
                padding: 6,
              }}
              preview={{
                mask: (
                  <Space>
                    <EyeOutlined /> Ampliar imagen
                  </Space>
                ),
              }}
              fallback="assets/images/no-data-image.svg"
            />
          </div>

          {/* Tiras de miniaturas si hay más de 1 imagen */}
          {productImages.length > 1 && (
            <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
              {productImages.map((img: any, idx: number) => (
                <div
                  key={img.uid || img.id || idx}
                  onClick={() => setActiveImgIndex(idx)}
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: 4,
                    overflow: "hidden",
                    cursor: "pointer",
                    border: activeImgIndex === idx ? "2px solid #1890ff" : "1px solid #d9d9d9",
                    flexShrink: 0,
                    background: "#ffffff",
                    position: "relative",
                  }}
                >
                  <img
                    src={img.file_url}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  {img.is_primary && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        background: "#faad14",
                        color: "#fff",
                        fontSize: 8,
                        padding: "0 2px",
                        lineHeight: "10px",
                        borderRadius: "2px 0 0 0",
                      }}
                    >
                      ★
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {/* Tarjeta de detalle */}
      <div style={{ background: "#fafafa", borderRadius: 8, padding: "4px 16px", marginBottom: 20 }}>
        <InfoRow icon={<AppstoreOutlined />} label="Tipo">
          <Tag color="blue">{getTypeName(item.register_type)}</Tag>
        </InfoRow>

        <InfoRow icon={<TagOutlined />} label="Categoría">
          {getCategoryName(item.category)}
        </InfoRow>

        {item.description && (
          <InfoRow icon={<FileTextOutlined />} label="Descripción">
            <span style={{ ...valueStyle, fontWeight: 400 }}>{item.description}</span>
          </InfoRow>
        )}

        {item.register_type !== "SERVICE" && (
          <InfoRow icon={<InboxOutlined />} label="Existencias">
            {renderStock(item.inventory || [], item.register_type)}
          </InfoRow>
        )}

        {item.location && (
          <InfoRow icon={<EnvironmentOutlined />} label="Ubicación">
            {item.location}
          </InfoRow>
        )}

        {item.barcode && (
          <InfoRow icon={<BarcodeOutlined />} label="Código de barras">
            {item.barcode}
          </InfoRow>
        )}

        {item.sell_item && (
          <InfoRow icon={<DollarOutlined />} label="Precio de venta">
            {formatCOP(getSellPrice())}
          </InfoRow>
        )}

        {item.purchase_item && (
          <InfoRow icon={<DollarOutlined />} label="Precio de compra">
            {formatCOP(getPurchasePrice())}
          </InfoRow>
        )}

        {item.is_traceable && (
          <InfoRow icon={<DeploymentUnitOutlined style={{ color: "#1d39c4" }} />} label="Trazabilidad">
            <Tag color="blue">Trazabilidad Individual Habilitada</Tag>
          </InfoRow>
        )}

        {/* Otros valores */}
        {item.other_values?.filter((v: any) => v.is_visible && v.field_name && v.field_value).map((v: any) => (
          <InfoRow key={v.id} icon={<FileTextOutlined />} label={v.field_name}>
            {v.field_value}
          </InfoRow>
        ))}
      </div>

      {/* Acciones */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        {canEdit && item.register_type !== "SERVICE" && onFractionate && (
          <Button icon={<SwapOutlined />} onClick={onFractionate}>
            Fraccionar / Desglose
          </Button>
        )}
        {canEdit && (
          <Button type="primary" icon={<EditOutlined />} onClick={onEdit}>
            Editar
          </Button>
        )}
      </div>
    </div>
  )
}

export default ViewProductReadOnly
